/**
 * Portado do abcx-web (staging 70989884) para o wazir-web; o DTO vem de
 * `GET permission-groups/front-first-tree` (wazir-api) — campos extras do motor do
 * Wazir/Nexus (`techFront`, `description`, `menuIds`, `advancedOnly`) são opcionais.
 *
 * Tipos do seletor de permissões "front-first" (Fase B — ver
 * specs/permissions/front-first-permission-groups.md §10). Espelha o DTO
 * devolvido por `GET main/permission-groups/front-first-tree` (abcx-api,
 * `front-first-tree-resolver.ts`). A árvore já vem com os
 * `system_action_param.id` REAIS resolvidos (mesmo espaço de ids que
 * `PermissionGroupDto.permissionIds`/`UserPermissionAssignment` usam) — este
 * componente é só apresentação/seleção, sem lógica de resolução própria.
 */

export interface FrontFirstTreeActionDto {
  key: string;
  label: string;
  verb: string;
  /** União de todos os ids desta folha (front + back) — concedidos/revogados juntos. */
  ids: string[];
  frontIds: string[];
  backIds: string[];
  hasFront: boolean;
  hasBack: boolean;
  /** Nomes técnicos (visão de suporte) — o wazir-api zera no own-only. */
  techFront?: string[];
  techBack?: string[];
  /** PT-BR do que a permissão faz (tooltip). */
  description?: string;
}

export interface FrontFirstTreeSubgroupDto {
  key: string;
  label: string;
  pathLabel: string;
  routerLink: string | null;
  ids: string[];
  /** ids do PRÓPRIO `MENU_*` do nó (fecho de menu). */
  menuIds?: string[];
  actions: FrontFirstTreeActionDto[];
  pending?: boolean;
  note?: string;
  /**
   * Profundidade arbitrária (aninhamento recursivo) — sub-telas DENTRO desta
   * tela (ex.: CADASTRO > PRODUTOS > CATEGORIAS/MARCAS/...), mesma forma
   * `FrontFirstTreeSubgroupDto` (podendo ter os próprios `children`). `ids`
   * deste nó já vem do backend como a UNIÃO recursiva de suas próprias
   * ações + de TODOS os descendentes (`front-first-tree-resolver.ts`,
   * `resolveSubgroup`) — por isso o tri-state/contagem por checkbox
   * (`isSubgroupChecked`/`isSubgroupIndeterminate`/`toggleSubgroup`) não
   * precisa de nenhuma lógica nova: já funciona igual pra um nó-container
   * (sem ações próprias, só `children`) quanto para uma tela folha. Opcional
   * — ausente/`undefined` = tela-folha, comportamento idêntico ao de antes.
   */
  children?: FrontFirstTreeSubgroupDto[];
}

export interface FrontFirstTreeGroupDto {
  key: string;
  label: string;
  ids: string[];
  menuIds?: string[];
  subgroups: FrontFirstTreeSubgroupDto[];
  /** Grupo sem tela própria no menu (catch-all de back) — visual distinto no fim da lista. */
  advancedOnly?: boolean;
}

export interface FrontFirstTreeResponse {
  groups: FrontFirstTreeGroupDto[];
  totalIds: string[];
  unresolvedActionNames: string[];
}

/** Ícone Material por chave de grupo (área do menu) — aproxima o protótipo. */
export const FRONT_FIRST_GROUP_ICON: Record<string, string> = {
  pdv: 'point_of_sale',
  cadastro: 'inventory_2',
  relatorio: 'bar_chart',
  pessoas: 'groups',
  ferramentas: 'build',
  usuarios: 'admin_panel_settings',
  'alfasu-pay': 'payments',
  outros: 'more_horiz',
  // Ramos de módulo do reenquadramento do catch-all de back (abcx-api,
  // specs/abcx/back-avancado-reframe-by-module.md — front-first-group-tree.
  // builder.ts, `resolveBackModule`): mesmas chaves geradas lá.
  ifood: 'delivery_dining',
  'mercado-livre': 'storefront',
  fiscal: 'receipt_long',
  alfasupay: 'payments',
  pizza: 'local_pizza',
  impressao: 'print',
  'sistema-interno': 'settings_applications',
  avancado: 'unpublished',
};

export function frontFirstGroupIcon(key: string): string {
  return FRONT_FIRST_GROUP_ICON[key] || 'folder';
}
